if (!customElements.get('cart-note')) {
    customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();
        
        this.errorElements = {
          maxLength: this.querySelector(".cart-note-error"),          
          emoji: this.querySelector(".cart-note-error-emoji")
        };
        
        const marketElement = this.querySelector('.customer-market-mobile') || 
                              document.getElementById("customer-market");
        this.market = marketElement ? marketElement.value.trim() : "default";
        
        this.forbiddenRegex = this.buildForbiddenRegex();        
        this.emojiRegex = /[\p{Extended_Pictographic}]/gu;
        
        this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
        this.isMobileInstance = this.classList.contains('cart__note--mobile');
        
        // References for component elements
        this.cartNoteItem = document.querySelector('.cart-note-item');
        this.textarea = this.querySelector('textarea');
        this.savedNoteDisplay = document.getElementById('cart-note-saved');
        
        // Initialize controls based on instance type
        if (this.isMobileInstance) {
          this.initMobileModal();
        } else {
          this.initDesktopControls();
        }
        
        this.setupEventListeners();
        this.setupResponsiveListeners();
      }
      
      /**
       * Setup listeners for viewport changes
       */
      setupResponsiveListeners() {
        // Only desktop instance handles button clicks
        if (!this.isMobileInstance) {
          this.handleResponsiveButtonClick = (e, action) => {
            if (this.isMobile()) {
              e.preventDefault();
              e.stopPropagation();
                            
              if (action === 'add' || action === 'edit') {
                this.openMobileModal();
              } else if (action === 'remove') {
                this.removeNote();
              }
            }
          };
          
          // Rebind buttons with responsive checks
          if (this.addButton) {
            const originalAddHandler = () => {
              if (!this.isMobile()) {
                this.enterEditMode();
              }
            };
            this.addButton.addEventListener('click', (e) => {
              this.handleResponsiveButtonClick(e, 'add');
              if (!this.isMobile()) {
                this.enterEditMode();
              }
            });
          }
          
          if (this.editButton) {
            this.editButton.addEventListener('click', (e) => {
              this.handleResponsiveButtonClick(e, 'edit');
              if (!this.isMobile()) {
                this.originalValue = this.textarea?.value || '';
                this.enterEditMode();
              }
            });
          }
        }
      }
      
      /**
       * Check if currently in mobile viewport
       * @returns {boolean} True if in mobile viewport
       */
      isMobile() {
        return this.mobileMediaQuery.matches;
      }
      
      /**
       * Open mobile modal from desktop context
       */
      openMobileModal() {
        const mobileModal = document.getElementById('cart-note-modal');
        const mobileTextarea = document.querySelector('.cart__note--mobile textarea');
        const mobileInstance = document.querySelector('.cart__note--mobile');
        
        if (!mobileModal || !mobileTextarea || !mobileInstance) return;
        
        // Sync desktop value to mobile
        if (this.textarea) {
          mobileTextarea.value = this.textarea.value;
        }
        
        // Store scroll position
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        
        // Set body styles to prevent scroll
        document.body.style.top = `-${scrollPosition}px`;
        document.body.classList.add('modal-open');
        
        // Store scroll position in mobile instance
        if (mobileInstance.customElementInstance) {
          mobileInstance.customElementInstance.scrollPosition = scrollPosition;
        }
        
        // Trigger reflow to ensure transition works
        void mobileModal.offsetWidth;
        
        // Show modal - CSS handles all animations
        mobileModal.classList.add('is-active');
        
        // Update button state via mobile instance
        const cartNote = document.querySelector('.cart__note--mobile');
        if (cartNote && typeof cartNote.updateSaveButtonState === 'function') {
          cartNote.updateSaveButtonState();
        }
      }
      
      /**
       * Initialize mobile modal controls
       */
      initMobileModal() {
        this.modal = document.getElementById('cart-note-modal');
        this.modalOverlay = this.modal?.querySelector('.cart-note-modal__overlay');
        this.closeButton = this.modal?.querySelector('.cart-note-modal__close');
        this.saveButton = document.getElementById('cart-note-modal-save-button');
        this.discardButton = this.modal?.querySelector('.cart-note-modal__discard');
        
        // Mobile uses different add/edit buttons that trigger modal
        this.addButton = document.getElementById('cart-note-add-button');
        this.editButton = document.getElementById('cart-note-edit-button');
        this.removeButton = document.getElementById('cart-note-remove-button');
        
        // Store reference to this instance for cross-instance access
        if (this.closest('.cart__note--mobile')) {
          this.closest('.cart__note--mobile').customElementInstance = this;
        }
        
        this.setupMobileHandlers();
      }
      
      /**
       * Initialize desktop controls
       */
      initDesktopControls() {
        this.addButton = document.getElementById('cart-note-add-button');
        this.saveButton = document.getElementById('cart-note-save-button');
        this.editButton = document.getElementById('cart-note-edit-button');
        this.removeButton = document.getElementById('cart-note-remove-button');
        
        // Store original value to discard changes
        this.originalValue = this.textarea?.value || '';
        
        this.setupDesktopHandlers();
      }
      
      /**
       * Build forbidden characters regex from data attribute
       * Escapes special regex characters to create a valid character class
       * @returns {RegExp|null} Regular expression to match forbidden characters, or null if none configured
       */
      buildForbiddenRegex() {
        const prohibitedChars = this.dataset.prohibitedChars;
        
        if (!prohibitedChars || prohibitedChars.trim() === '') {
          return null;
        }
        
        // Escape special regex characters for use in character class
        const escapedChars = prohibitedChars
          .split('')
          .map(char => {
            // Characters that need escaping in character class: ] \ ^ -
            if ([']', '\\', '^', '-'].includes(char)) {
              return '\\' + char;
            }
            return char;
          })
          .join('');
        
        return new RegExp(`[${escapedChars}]`, 'g');
      }
      
      debounce(func, delay) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => func(...args), delay);
        };
      }
      
      setupEventListeners() {
        this.addEventListener('input', (event) => {
          const validatedValue = this.validateInput(event.target.value);
          event.target.value = validatedValue;
        });
      }
      
      /**
       * Setup mobile modal event handlers
       */
      setupMobileHandlers() {
        if (this.closeButton) {
          this.closeButton.addEventListener('click', () => {
            this.closeModal();
          });
        }
        
        if (this.modalOverlay) {
          this.modalOverlay.addEventListener('click', () => {
            this.closeModal();
          });
        }
        
        if (this.saveButton) {
          this.saveButton.addEventListener('click', async () => {
            await this.saveMobileNote();
          });
        }
        
        if (this.removeButton) {
          this.removeButton.addEventListener('click', async (e) => {
            if (this.isMobile()) {
              e.preventDefault();
              await this.removeNote();
            }
          });
        }
        
        // Setup textarea focus/blur handlers for button visibility
        if (this.textarea) {
          this.textarea.addEventListener('input', () => {
            this.updateSaveButtonState();
          });
        }
      }
      
      /**
       * Setup desktop button handlers
       */
      setupDesktopHandlers() {
        if (this.saveButton) {
          this.saveButton.addEventListener('click', () => {
            if (!this.isMobile()) {
              this.saveNote();
            }
          });
        }
        
        if (this.removeButton) {
          this.removeButton.addEventListener('click', async () => {
            if (!this.isMobile()) {
              await this.removeNote();
            }
          });
        }
      }
      
      /**
       * Open mobile modal
       */
      openModal() {
        if (!this.modal) return;
        
        const desktopTextarea = document.querySelector('.cart__note--desktop textarea');
        if (desktopTextarea && this.textarea) {
          this.textarea.value = desktopTextarea.value;
        }
        
        // Store scroll position before opening modal
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        
        // Set body position to prevent scroll jump
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.classList.add('modal-open');
        
        // Trigger reflow to ensure transition works
        void this.modal.offsetWidth;
        
        // Show modal - CSS handles all animations
        this.modal.classList.add('is-active');
        
        // Update button state based on textarea content
        this.updateSaveButtonState();
      }

      /**
       * Update save button state based on textarea content
       */
      updateSaveButtonState() {        
        if (!this.saveButton || !this.textarea) return;
        
        const hasContent = this.textarea.value.trim().length > 0;
        const desktopTextarea = document.querySelector('.cart__note--desktop textarea');
        const originalValue = desktopTextarea?.value || '';
        
        // Button enabled only if there's content
        if (hasContent) {
          this.saveButton.removeAttribute('disabled');
          // Update button text based on whether note exists
          const buttonText = this.saveButton.querySelector('span') || this.saveButton;
          if (originalValue.trim().length > 0) {
            buttonText.textContent = this.getSaveText();
          } else {
            buttonText.textContent = this.getAddText();
          }
        } else {
          this.saveButton.setAttribute('disabled', 'true');
          const buttonText = this.saveButton.querySelector('span') || this.saveButton;
          buttonText.textContent = this.getAddText();
        }
      }
      
      getAddText() {        
        if(typeof addNoteText != "undefined") {
          return addNoteText;
        }else {
          const addButton = document.getElementById('cart-note-add-button');
          return addButton?.querySelector('.cart-gift-wrap-item__button-text')?.textContent.trim()
        }
      }
      
      getSaveText() {        
        if(typeof saveNoteText != "undefined") {
          return saveNoteText;
        }else {
          const saveButton = document.getElementById('cart-note-save-button');
          return saveButton?.querySelector('span')?.textContent.trim()
        }
      }
      
      /**
       * Close mobile modal
       */
      closeModal() {
        if (!this.modal) return;
        
        this.modal.classList.remove('is-active');
        
        const isMobileResolution = this.mobileMediaQuery.matches;
        
        if (isMobileResolution) {
          // Wait for CSS transition to complete (300ms)
          setTimeout(() => {
            document.body.classList.remove('modal-open');
            
            // Restore scroll position
            document.body.style.top = '';
            if (typeof this.scrollPosition !== 'undefined') {
              window.scrollTo(0, this.scrollPosition);
            }
          }, 300);
        } else {
          document.body.classList.remove('modal-open');
          
          // Restore scroll position
          document.body.style.top = '';
          if (typeof this.scrollPosition !== 'undefined') {
            window.scrollTo(0, this.scrollPosition);
          }
        }
      }

      /**
       * Save note from mobile modal
       */
      async saveMobileNote() {
        if (!this.textarea) return;
        
        const currentValue = this.textarea.value;
        
        await this.updateCartNote(currentValue);
        
        // Sync with desktop textarea
        const desktopTextarea = document.querySelector('.cart__note--desktop textarea');
        if (desktopTextarea) {
          desktopTextarea.value = currentValue;
        }
        
        if (this.savedNoteDisplay) {
          this.savedNoteDisplay.textContent = currentValue;
        }
        
        if (this.cartNoteItem) {
          if (currentValue.trim() !== '') {
            this.cartNoteItem.classList.add('cart-note-item--added');
          } else {
            this.cartNoteItem.classList.remove('cart-note-item--added');
          }
        }
        
        this.closeModal();
      }
      
      /**
       * Discard changes in mobile modal
       */
      discardMobileChanges() {
        const desktopTextarea = document.querySelector('.cart__note--desktop textarea');
        const originalValue = desktopTextarea?.value || '';
        
        if (this.textarea) {
          this.textarea.value = originalValue;
        }
        
        this.updateSaveButtonState();
        this.closeModal();
      }
      
      /**
       * Enter edit mode by adding editing class to parent
       */
      enterEditMode() {
        if (!this.cartNoteItem) return;
        
        this.cartNoteItem.classList.add('cart-note-item--editing');
        
        if (this.textarea) {
          this.textarea.focus();
        }
      }
      
      /**
       * Discard changes and restore original state
       * Removes both editing and added classes from container
       */
      discardChanges() {
        if (!this.cartNoteItem) return;
        
        if (this.textarea) {
          this.textarea.value = this.originalValue;
        }
        
        this.cartNoteItem.classList.remove('cart-note-item--editing');
        this.cartNoteItem.classList.remove('cart-note-item--added');
      }
      
      /**
       * Save note and exit edit mode
       * Adds 'added' class and removes 'editing' class from container
       */
      async saveNote() {
        if (!this.textarea) return;
        
        const currentValue = this.textarea.value;
        
        await this.updateCartNote(currentValue);
        
        if (this.savedNoteDisplay) {
          this.savedNoteDisplay.textContent = currentValue;
        }
        
        this.originalValue = currentValue;
        
        if (this.cartNoteItem) {
          this.cartNoteItem.classList.remove('cart-note-item--editing');
          
          if (currentValue.trim() !== '') {
            this.cartNoteItem.classList.add('cart-note-item--added');
          } else {
            this.cartNoteItem.classList.remove('cart-note-item--added');
          }
        }
      }
      
      /**
       * Remove note and reset to initial state
       */
      async removeNote() {        
        const desktopTextarea = document.querySelector('.cart__note--desktop textarea');
        const mobileTextarea = document.querySelector('.cart__note--mobile textarea');
        
        if (desktopTextarea) {
          desktopTextarea.value = '';
        }
        
        if (mobileTextarea) {
          mobileTextarea.value = '';
        }
        
        await this.updateCartNote('');
        
        if (this.savedNoteDisplay) {
          this.savedNoteDisplay.textContent = '';
        }
        
        this.originalValue = '';
        
        if (this.cartNoteItem) {
          this.cartNoteItem.classList.remove('cart-note-item--editing');
          this.cartNoteItem.classList.remove('cart-note-item--added');
        }
        
        // Close modal if open
        const mobileModal = document.getElementById('cart-note-modal');
        if (mobileModal && mobileModal.classList.contains('is-active')) {
          this.closeModal();
        }
      }
      
      validateInput(value) {
        this.hideAllErrors();
        
        const validationResult = {
          value: value,
          hasForbiddenChars: this.forbiddenRegex ? this.forbiddenRegex.test(value) : false,
          hasEmoji: this.emojiRegex.test(value),
          exceedsMaxLength: value.length > 140
        };
        
        if (this.market === "KR") {
          return this.validateKoreanMarket(validationResult);
        }
        
        return this.validateDefaultMarket(validationResult);
      }
      
      validateKoreanMarket(validationResult) {
        let { value, hasEmoji, exceedsMaxLength } = validationResult;
        
        if (hasEmoji) {
          value = value.replace(this.emojiRegex, '');
          this.showError('emoji');
        }
        
        if (exceedsMaxLength) {
          value = value.substring(0, 140);
          this.showError('maxLength');
        }
        
        return value;
      }
      
      validateDefaultMarket(validationResult) {
        let { value, hasForbiddenChars, hasEmoji, exceedsMaxLength } = validationResult;
        
        if (hasForbiddenChars && this.forbiddenRegex) {
          value = value.replace(this.forbiddenRegex, '');
          this.showError('forbiddenChars');
        }
        
        if (hasEmoji) {
          value = value.replace(this.emojiRegex, '');
          this.showError('emoji');
        }
        
        if (exceedsMaxLength) {
          value = value.substring(0, 140);
          this.showError('maxLength');
        }
        
        return value;
      }
      
      hideAllErrors() {
        Object.values(this.errorElements).forEach(element => {
          if (element && !element.classList.contains("hidden")) {
            element.classList.add("hidden");
          }
        });
      }
      
      showError(errorType) {
        const element = this.errorElements[errorType];
        if (element) {
          element.classList.remove("hidden");
        }
      }
      
      saveValue = this.debounce(async (value) => {
        await this.updateCartNote(value);
      }, ON_CHANGE_DEBOUNCE_TIMER);
      
      async updateCartNote(value) {        
        if(this.saveButton && typeof setButtonLoading === "function"){
          setButtonLoading(this.saveButton.id, true);
        }
        const body = JSON.stringify({ note: value });
        await fetch(`${routes.cart_update_url}`, { 
          ...fetchConfig(), 
          ...{ body } 
        });

        if(this.saveButton && typeof setButtonLoading === "function"){
          setButtonLoading(this.saveButton.id, false);
        }
        
        if(value != "" && typeof show_float_cart === "function") {
          show_float_cart("card_note")
        }        
      }
      
      async saveCurrentValue() {
        const currentValue = this.querySelector('textarea').value;
        await this.saveValue(currentValue);
      }
    });
}